import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import type {
  BoardMemberRole,
  BoardPermission,
  BoardRoleMatrix,
  BoardTemplate,
  ProjectMemberItem,
} from '@hygge/shared';
import { boardRoleLevel, grantableBoardRoles } from '@hygge/shared';
import {
  getBoards,
  getBoard,
  createBoard,
  updateBoard,
  deleteBoard,
  suggestBoardSlug,
  getBoardMembers,
  addBoardMembers,
  changeBoardMemberRole,
  removeBoardMember,
  getBoardRoleMatrix,
  setBoardRoleMatrix,
  resetBoardRoleMatrix,
} from '@/lib/boards';
import { getEmployees } from '@/lib/employees';
import { useBoardSettings } from '@/hooks/use-tasks';
import { projectsKeys } from '@/hooks/use-projects';
import { useMemo } from 'react';

export const boardKeys = {
  all: ['boards'] as const,
  lists: () => [...boardKeys.all, 'list'] as const,
  list: (params?: { projectId?: string }) => [...boardKeys.lists(), params] as const,
  details: () => [...boardKeys.all, 'detail'] as const,
  detail: (id: string) => [...boardKeys.details(), id] as const,
  roleMatrix: (id: string) => [...boardKeys.detail(id), 'role-matrix'] as const,
};

export function useBoards(params?: { projectId?: string; search?: string }, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: boardKeys.list(params),
    queryFn: () => getBoards(params),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: options.enabled ?? true,
  });
}

export function useBoard(id: string | undefined) {
  return useQuery({
    queryKey: boardKeys.detail(id ?? ''),
    queryFn: () => getBoard(id!),
    enabled: !!id,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateBoard() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      name: string;
      slug: string;
      description?: string;
      color?: string;
      icon?: string;
      projectId?: string;
      projectMemberIds?: string[];
      departmentId?: string;
      template?: BoardTemplate;
      addDepartmentMembers?: boolean;
      visibility?: 'public' | 'members_only';
    }) => createBoard(payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: boardKeys.lists() });
    },
  });
}

export function useUpdateBoard() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: {
      id: string;
      name?: string;
      summary?: string;
      description?: string;
      color?: string;
      icon?: string;
      visibility?: string;
      status?: string;
      priority?: string;
      targetDate?: string | null;
      leadId?: string | null;
      projectId?: string | null;
      projectMemberIds?: string[];
    }) => updateBoard(id, payload),
    // The project the board was linked to before this save, read from the
    // cache: its Activity changes too when the board moves away.
    onMutate: (variables) => ({
      previousProjectId: qc.getQueryData<{ projectId?: string | null }>(boardKeys.detail(variables.id))?.projectId ?? null,
    }),
    onSuccess: (_data, variables, context) => {
      qc.invalidateQueries({ queryKey: boardKeys.lists() });
      qc.invalidateQueries({ queryKey: boardKeys.detail(variables.id) });
      if (variables.projectMemberIds?.length) {
        qc.invalidateQueries({ queryKey: boardMemberKeys.list(variables.id) });
      }
      // A project's Activity carries the lines of the boards linked to it,
      // so linking, moving or unlinking changes the old and the new one's.
      if (variables.projectId !== undefined) {
        for (const projectId of [context?.previousProjectId, variables.projectId]) {
          if (projectId) qc.invalidateQueries({ queryKey: projectsKeys.activity(projectId) });
        }
      }
      // Overview reads from its own aggregate query — refresh it too.
      qc.invalidateQueries({ queryKey: ['board-overview', variables.id] });
    },
  });
}

export function useDeleteBoard() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteBoard(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardKeys.lists() });
    },
  });
}

export function useBoardSlugSuggest(name: string) {
  return useQuery({
    queryKey: ['board-slug-suggest', name],
    queryFn: () => suggestBoardSlug(name),
    enabled: name.length >= 1,
    staleTime: CACHE_TIME.SHORT,
  });
}

// ─── Members ───────────────────────────────────────────────────────────

export const boardMemberKeys = {
  all: ['board-members'] as const,
  list: (boardId: string) => [...boardMemberKeys.all, boardId] as const,
};

export function useBoardMembers(boardId: string | undefined) {
  return useQuery({
    queryKey: boardMemberKeys.list(boardId ?? ''),
    queryFn: () => getBoardMembers(boardId!),
    enabled: !!boardId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useAddBoardMembers(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    // `role` absent is the server's default, `member`. Somebody listed only
    // through the project is given a role the same way: a board row of their
    // own, which outranks the project tier.
    mutationFn: ({ employeeIds, role }: { employeeIds: string[]; role?: BoardMemberRole }) =>
      addBoardMembers(boardId!, employeeIds, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardMemberKeys.list(boardId ?? '') });
      // Overview aggregate carries the member list shown in the sidebar/header.
      qc.invalidateQueries({ queryKey: ['board-overview', boardId ?? ''] });
    },
  });
}

export function useChangeBoardMemberRole(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, role }: { employeeId: string; role: string }) =>
      changeBoardMemberRole(boardId!, employeeId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardMemberKeys.list(boardId ?? '') });
      qc.invalidateQueries({ queryKey: ['board-overview', boardId ?? ''] });
    },
  });
}

export function useRemoveBoardMember(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: string) => removeBoardMember(boardId!, employeeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardMemberKeys.list(boardId ?? '') });
      // Overview aggregate carries the member list shown in the sidebar/header.
      qc.invalidateQueries({ queryKey: ['board-overview', boardId ?? ''] });
    },
  });
}

// ─── Assignable Members (respects allowOrgWideAssignees setting) ──────

export function useAssignableMembers(boardId: string | undefined) {
  const { data: settings } = useBoardSettings(boardId);
  const orgWide = settings?.allowOrgWideAssignees === true;

  const { data: members } = useBoardMembers(boardId);
  const { data: employeesData } = useQuery({
    queryKey: ['assignable-employees', boardId],
    queryFn: () => getEmployees({ status: 'active', limit: 500 }),
    enabled: !!boardId && orgWide,
    staleTime: CACHE_TIME.DEFAULT,
  });

  return useMemo(() => {
    if (!orgWide) return members;

    return employeesData?.data?.map((e): ProjectMemberItem => ({
      id: e.id,
      employeeId: e.id,
      firstName: e.firstName,
      lastName: e.lastName,
      avatarUrl: e.avatarUrl ?? null,
      departmentName: e.department?.name ?? null,
      location: null,
      role: null,
      isManager: false,
      joinedAt: '',
      removedAt: null,
    }));
  }, [orgWide, members, employeesData]);
}

/**
 * What the current user may do on THIS board.
 *
 * Not `usePermission`: a company-wide code says whether somebody reaches the
 * Boards section, and nothing about what happens inside one board. The same
 * person may create sprints here and not next door, because each board keeps
 * its own table of what its four roles may do.
 *
 * Answers `false` while the board is loading. A control that appears and then
 * vanishes is worse than one that arrives a moment late.
 */
export function useBoardPermission(
  boardId: string | undefined,
  permission: BoardPermission,
): boolean {
  const { data } = useBoard(boardId);
  return data?.currentUserPermissions?.includes(permission) ?? false;
}

/** True when the current user holds the board's Owner rank — not a permission. */
export function useIsBoardOwner(boardId: string | undefined): boolean {
  const { data } = useBoard(boardId);
  return data?.currentUserRole === 'owner';
}

export function useBoardRoleMatrix(boardId: string | undefined) {
  return useQuery({
    queryKey: boardKeys.roleMatrix(boardId ?? ''),
    queryFn: () => getBoardRoleMatrix(boardId!),
    enabled: !!boardId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useSetBoardRoleMatrix(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<BoardRoleMatrix>) => setBoardRoleMatrix(boardId, payload),
    onSuccess: async () => {
      // The board detail carries the caller's own effective set, so every gate
      // in the UI has to re-read it — including the ones on this screen.
      await Promise.all([
        qc.invalidateQueries({ queryKey: boardKeys.roleMatrix(boardId) }),
        qc.invalidateQueries({ queryKey: boardKeys.detail(boardId) }),
        qc.invalidateQueries({ queryKey: boardKeys.lists() }),
      ]);
    },
  });
}

export function useResetBoardRoleMatrix(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resetBoardRoleMatrix(boardId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: boardKeys.roleMatrix(boardId) }),
        qc.invalidateQueries({ queryKey: boardKeys.detail(boardId) }),
        qc.invalidateQueries({ queryKey: boardKeys.lists() }),
      ]);
    },
  });
}

/** The caller's governance rank on this board, or null when they hold none. */
export function useBoardRole(boardId: string | undefined): BoardMemberRole | null {
  const { data } = useBoard(boardId);
  return (data?.currentUserRole as BoardMemberRole | null) ?? null;
}

/**
 * The roles this caller may hand out here: their own rank and below.
 *
 * The server caps a grant at the caller’s own tier and refuses changing
 * anybody who outranks them, so a picker listing all four offers three
 * refusals to a Member and one to an Admin.
 */
export function useGrantableBoardRoles(boardId: string | undefined): BoardMemberRole[] {
  const role = useBoardRole(boardId);
  return useMemo(() => grantableBoardRoles(role), [role]);
}

/**
 * Whether somebody holding `mine` may change somebody holding `theirs`.
 *
 * The server refuses changing anybody who OUTRANKS the caller, so an equal
 * rank is allowed — an Admin may change another Admin. Somebody with no rank
 * may change nobody. Pass the target's effective role — the one the board
 * gives them. A null `theirs` means no role at all, and nothing to change;
 * somebody listed without a row of their own (through the project, or as
 * creator) still has an effective role, and is changed by giving them a row.
 */
export function mayChangeBoardMember(
  mine: string | null | undefined,
  theirs: string | null | undefined,
): boolean {
  if (boardRoleLevel(mine) === 0) return false;
  if (boardRoleLevel(theirs) === 0) return false;
  return boardRoleLevel(theirs) <= boardRoleLevel(mine);
}

/** True when the caller may change somebody who currently holds `role`. */
export function useMayChangeBoardMember(boardId: string | undefined) {
  const mine = useBoardRole(boardId);
  return useMemo(() => (theirs: string | null | undefined) => mayChangeBoardMember(mine, theirs), [mine]);
}

/**
 * What each tab of Board settings asks this board for.
 *
 * A named map rather than five inline calls, because this is where the rule
 * "do not offer a control the board would refuse" actually lives, and a hook
 * cannot be tested here. Permissions is absent on purpose: it follows the
 * Owner rank, not a permission.
 */
export const BOARD_SETTINGS_TAB_PERMISSION: Record<
  'columns' | 'tags' | 'members' | 'settings' | 'visibility' | 'deleteBoard' | 'automations',
  BoardPermission
> = {
  columns: 'column.manage',
  tags: 'tag.manage',
  members: 'member.manage',
  settings: 'board.settings',
  visibility: 'visibility.manage',
  deleteBoard: 'board.delete',
  automations: 'automation.manage',
};

export interface BoardSettingsTabs {
  columns: boolean;
  tags: boolean;
  members: boolean;
  /** Board properties, the defaults and the auto-archive rule. */
  settings: boolean;
  /** Public <-> Private. Stands on its own: it does not also need `settings`. */
  visibility: boolean;
  /** Deleting the board. Stands on its own too, and no preset gives it to an Admin. */
  deleteBoard: boolean;
  automations: boolean;
  rolePermissions: boolean;
  /**
   * Adding the people of the board's project (HW-983): `member.manage` on a
   * board linked to a project. Its control sits in General's Project section.
   */
  projectPeople: boolean;
}

/**
 * Which tab the dialog opens on: the first one this caller has, in the order
 * the dialog lists them. Undefined when they have none, which is what the
 * control that opens the dialog reads.
 *
 * A pure function because the order has to match the tab strip by hand, and a
 * hook cannot be tested here.
 */
export function firstBoardSettingsTab(tabs: BoardSettingsTabs): string | undefined {
  return (
    (tabs.columns && 'columns') ||
    (tabs.tags && 'tags') ||
    (tabs.members && 'members') ||
    (hasGeneralTab(tabs) && 'general') ||
    (tabs.automations && 'automations') ||
    (tabs.rolePermissions && 'role-permissions') ||
    undefined
  );
}

/**
 * The General tab holds four separately grantable things: the board’s
 * properties, its visibility, its deletion, and adding the people of the
 * project it is linked to. A board may hand out the others without the first
 * — the backend asks for each on its own — so gating the whole tab on the
 * properties permission would withhold controls the board allows. The
 * sections inside it carry their own gates.
 */
export function hasGeneralTab(tabs: BoardSettingsTabs): boolean {
  return tabs.settings || tabs.visibility || tabs.deleteBoard || tabs.projectPeople;
}

/**
 * Which tabs of Board settings this caller has, in the order the dialog shows
 * them, and which one it should open on.
 *
 * The dialog and the control that opens it read the same answer, so the gear
 * cannot open a dialog with nothing in it, and the dialog cannot open on a tab
 * the caller does not have. `first` is undefined when they have none.
 */
export function useBoardSettingsTabs(boardId: string | undefined) {
  const P = BOARD_SETTINGS_TAB_PERMISSION;
  const { data: board } = useBoard(boardId);
  const members = useBoardPermission(boardId, P.members);
  const tabs: BoardSettingsTabs = {
    columns: useBoardPermission(boardId, P.columns),
    tags: useBoardPermission(boardId, P.tags),
    members,
    settings: useBoardPermission(boardId, P.settings),
    visibility: useBoardPermission(boardId, P.visibility),
    deleteBoard: useBoardPermission(boardId, P.deleteBoard),
    automations: useBoardPermission(boardId, P.automations),
    rolePermissions: useIsBoardOwner(boardId),
    projectPeople: members && !!board?.projectId,
  };

  return { ...tabs, general: hasGeneralTab(tabs), first: firstBoardSettingsTab(tabs) };
}
/**
 * True when at least one board the caller can see allows `permission`.
 *
 * For the cross-board screens, where no single board answers the question: All
 * Issues lets somebody act on tasks from several boards at once, so the control
 * belongs on screen if they may do it anywhere, and the per-board check refuses
 * the parts of a selection that reach somewhere they may not.
 */
export function useAnyBoardPermission(permission: BoardPermission): boolean {
  const { data } = useBoards();
  return data?.some((b) => b.currentUserPermissions?.includes(permission)) ?? false;
}
